// Host game-settings editor (gather/apply config + the map & movement pickers).
// These functions only read/write DOM inputs + shared state; the wiring that
// reacts to changes lives in main.ts.

import {
  DEFAULT_GAME_CONFIG,
  HAZARD_TYPES,
  POWERUP_DEFS,
  POWERUP_TYPES,
  WALL_STYLES,
  gameConfigWithDefaults,
  type AdvancedConfig,
  type CtfScoreMode,
  type FlagStealMode,
  type FogVisionMode,
  type GameConfig,
  type GameMode,
  type HazardType,
  type MapSize,
  type PowerupType,
  type WallStyle,
} from "../../shared/protocol/index.js";
import { $ } from "../dom.js";
import { WALL_LABEL } from "./labels.js";
import { state } from "../state.js";

export const ADV_KEYS = Object.keys(DEFAULT_GAME_CONFIG.adv) as (keyof AdvancedConfig)[];

/**
 * Generate the per-power-up tuning inputs in the Advanced panel from the
 * registry, so a new power-up's config appears automatically (ids are
 * `adv-<key>`, matching ADV_KEYS / gatherAdvanced / applyConfigToControls).
 */
export function buildPowerupAdvInputs(): void {
  $("adv-powerups").innerHTML = POWERUP_DEFS.filter((d) => d.config.length > 0)
    .map((def) => {
      let rows = "";
      for (let i = 0; i < def.config.length; i += 2) {
        const pair = def.config.slice(i, i + 2);
        rows +=
          `<div class="row${pair.length === 2 ? " two" : ""}">` +
          pair
            .map(
              (fld) =>
                `<label for="adv-${fld.key}">${fld.label}</label>` +
                `<input id="adv-${fld.key}" type="number" min="${fld.min}" max="${fld.max}" step="${fld.step}" />`
            )
            .join("") +
          `</div>`;
      }
      // The data-pup wrapper lets applyModeVisibility hide a power-up's tuning
      // while its spawn toggle is off.
      return `<div class="adv-pup" data-pup="${def.id}"><h4 class="adv-h">Power-ups · ${def.label}</h4>${rows}</div>`;
    })
    .join("");
}

/**
 * Generate the spawn-type toggle list from the registry, so a new power-up
 * appears automatically (ids are `pup-<id>`, matching the gatherConfig /
 * applyConfigToControls loops over POWERUP_TYPES). Each row is a label
 * wrapping a hidden checkbox, so clicking anywhere on it toggles. Default
 * checked, mirroring DEFAULT_GAME_CONFIG.powerupTypes (all enabled).
 */
export function buildPowerupTypeToggles(): void {
  $("pup-types").innerHTML = POWERUP_DEFS.map(
    (def) =>
      `<label class="tog-item"><input type="checkbox" id="pup-${def.id}" checked />` +
      `<span class="tog-emblem" style="color:${def.color}">${def.emblem}</span>` +
      `<span class="tog-name">${def.label}</span><span class="tog-state"></span></label>`
  ).join("");
}

/**
 * Pair every numeric input in the host editor with a range slider (Photoshop
 * style: type a value or drag the slider next to it). The min/max/step come
 * from the number input, so registry-generated Advanced inputs get sliders
 * too. Dragging live-mirrors into the number field; the slider's native
 * change event (fired on release) bubbles to the #lobby-config listener and
 * commits the config exactly like typing does. Run once, after the generated
 * inputs exist (buildPowerupAdvInputs).
 */
export function enhanceNumberInputs(): void {
  $("lobby-config")
    .querySelectorAll<HTMLInputElement>('input[type="number"]')
    .forEach((num) => {
      if (num.closest(".num-combo") || num.min === "" || num.max === "") return;
      const wrap = document.createElement("span");
      wrap.className = "num-combo";
      num.replaceWith(wrap);
      // Pill-style stepper field: [−] value [+] (native spinners are hidden in CSS).
      const field = document.createElement("span");
      field.className = "num-field";
      const stepBtn = (dir: 1 | -1) => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "num-step";
        b.textContent = dir > 0 ? "+" : "−";
        b.tabIndex = -1;
        b.setAttribute("aria-hidden", "true");
        b.onclick = () => {
          if (dir > 0) num.stepUp();
          else num.stepDown();
          num.dispatchEvent(new Event("input")); // mirror into the slider
          num.dispatchEvent(new Event("change", { bubbles: true })); // -> updateConfig
        };
        return b;
      };
      const slider = document.createElement("input");
      slider.type = "range";
      slider.min = num.min;
      slider.max = num.max;
      slider.step = num.step || "1";
      slider.value = num.value;
      // The number field is the accessible control; slider + steppers are pointer aids.
      slider.tabIndex = -1;
      slider.setAttribute("aria-hidden", "true");
      field.append(stepBtn(-1), num, stepBtn(1));
      wrap.append(field, slider);
      slider.addEventListener("input", () => (num.value = slider.value));
      num.addEventListener("input", () => (slider.value = num.value));
    });
}

/** Re-mirror sliders after values are written programmatically (no input event). */
function syncNumberSliders(): void {
  $("lobby-config")
    .querySelectorAll<HTMLInputElement>('.num-combo input[type="number"]')
    .forEach((num) => {
      const slider = num
        .closest(".num-combo")
        ?.querySelector<HTMLInputElement>('input[type="range"]');
      if (slider) slider.value = num.value;
    });
}

export function gatherAdvanced(): AdvancedConfig {
  const d = DEFAULT_GAME_CONFIG.adv;
  const out = {} as AdvancedConfig;
  for (const k of ADV_KEYS) {
    const v = Number(($(`adv-${k}`) as HTMLInputElement).value);
    out[k] = Number.isFinite(v) ? v : d[k];
  }
  return out;
}

export function gatherConfig(): { maxPlayers: number; config: GameConfig } {
  const d = DEFAULT_GAME_CONFIG;
  const sel = (id: string) => ($(id) as HTMLSelectElement).value;
  const num = (id: string, d: number) => Number(($(id) as HTMLInputElement).value) || d;
  const checked = (id: string) => ($(id) as HTMLInputElement).checked;
  const hazardTypes = HAZARD_TYPES.filter((t) => checked(`hazard-${t}`)) as HazardType[];
  const powerupTypes = POWERUP_TYPES.filter((t) => checked(`pup-${t}`)) as PowerupType[];
  return {
    maxPlayers: num("max-players", 8),
    config: {
      mode: sel("mode") as GameMode,
      wallStyle: sel("walls") as WallStyle,
      mapSize: sel("map-size") as MapSize,
      rounds: num("rounds", 3),
      allowLateJoin: checked("allow-late"),
      tankSpeedPct: num("tank-speed", 100),
      hp: num("hp", 1),
      lives: Number(($("lives") as HTMLInputElement).value) || 0,
      respawnSeconds: num("cfg-respawn", 3),
      killPoints: num("kill-points", 60),
      deathPenaltyPct: Number(($("death-penalty") as HTMLInputElement).value) || 0,
      // Conquest/carry reuse winScore as their points-to-win (their own field);
      // other modes use the standard score-to-win.
      winScore:
        sel("mode") === "ctf" && (sel("ctf-score-mode") === "conquest" || sel("ctf-score-mode") === "carry")
          ? num("ctf-points", 300)
          : num("win-score", 300),
      // CTF picks 2 or 4 from its own selector; Team VS uses the 2–4 spinner.
      teamCount: sel("mode") === "ctf" ? num("ctf-team-count", 2) : num("team-count", 2),
      friendlyFire: checked("friendly-fire"),
      teamKillPenalty: Number(($("team-kill") as HTMLInputElement).value) || 0,
      teamSpawnZones: checked("team-spawn-zones"),
      maxFlags: num("max-flags", 3),
      flagTeamCarry: checked("flag-team-carry"),
      flagStealMode: sel("flag-steal") as FlagStealMode,
      flagsPerRound: num("flags-per-round", 1),
      ctfScoreMode: sel("ctf-score-mode") as CtfScoreMode,
      ctfRespawnBonus: num("ctf-respawn-bonus", 3),
      adv: gatherAdvanced(),
      fogOfWar: checked("fog-of-war"),
      visionRadius: num("vision-radius", d.visionRadius),
      fogBaseVision: sel("fog-base-vision") as FogVisionMode,
      fogFlagVision: sel("fog-flag-vision") as FogVisionMode,
      fogHideCarriedFlag: checked("fog-hide-carried"),
      hazardDensity: num("hazard-density", d.hazardDensity),
      hazardTypes,
      hazardDamage: num("hazard-damage", d.hazardDamage),
      hazardSlowMult: num("hazard-slow-mult", d.hazardSlowMult),
      hazardHealRate: num("hazard-heal-rate", d.hazardHealRate),
      destructibleWalls: checked("destructible-walls"),
      tankCollision: checked("tank-collision"),
      radar: checked("radar"),
      powerups: checked("powerups"),
      powerupEverySeconds: num("pwr-every", 8),
      powerupDespawnSeconds: num("pwr-despawn", 12),
      powerupCharges: num("pwr-charges", 1),
      powerupSpawnCount: num("pwr-count", 1),
      powerupTypes,
      powerupStacking: checked("pwr-stack"),
      combineWeapons: checked("pwr-combine"),
    },
  };
}

export function applyConfigToControls(c: GameConfig, maxPlayers: number): void {
  const cfg = gameConfigWithDefaults(c);
  const set = (id: string, v: string | number) => (($(id) as HTMLInputElement).value = String(v));
  const setChecked = (id: string, v: boolean) => (($(id) as HTMLInputElement).checked = v);
  set("max-players", maxPlayers);
  set("mode", cfg.mode);
  set("walls", cfg.wallStyle);
  set("map-size", cfg.mapSize);
  set("rounds", cfg.rounds);
  setChecked("allow-late", cfg.allowLateJoin);
  set("team-count", cfg.teamCount);
  set("ctf-team-count", cfg.teamCount >= 3 ? 4 : 2);
  set("tank-speed", cfg.tankSpeedPct);
  set("hp", cfg.hp);
  set("lives", cfg.lives);
  set("cfg-respawn", cfg.respawnSeconds);
  set("kill-points", cfg.killPoints);
  set("death-penalty", cfg.deathPenaltyPct);
  set("win-score", cfg.winScore);
  setChecked("friendly-fire", cfg.friendlyFire);
  set("team-kill", cfg.teamKillPenalty);
  setChecked("team-spawn-zones", cfg.teamSpawnZones);
  set("max-flags", cfg.maxFlags);
  setChecked("flag-team-carry", cfg.flagTeamCarry);
  set("flag-steal", cfg.flagStealMode);
  set("flags-per-round", cfg.flagsPerRound);
  set("ctf-score-mode", cfg.ctfScoreMode);
  set("ctf-points", cfg.winScore); // conquest points-to-win mirrors winScore
  set("ctf-respawn-bonus", cfg.ctfRespawnBonus);
  setChecked("fog-of-war", cfg.fogOfWar);
  set("vision-radius", cfg.visionRadius);
  set("fog-base-vision", cfg.fogBaseVision);
  set("fog-flag-vision", cfg.fogFlagVision);
  setChecked("fog-hide-carried", cfg.fogHideCarriedFlag);
  set("hazard-density", cfg.hazardDensity);
  for (const type of HAZARD_TYPES) setChecked(`hazard-${type}`, cfg.hazardTypes.includes(type));
  set("hazard-damage", cfg.hazardDamage);
  set("hazard-slow-mult", cfg.hazardSlowMult);
  set("hazard-heal-rate", cfg.hazardHealRate);
  setChecked("destructible-walls", cfg.destructibleWalls);
  setChecked("tank-collision", cfg.tankCollision);
  setChecked("radar", cfg.radar);
  setChecked("powerups", cfg.powerups);
  set("pwr-every", cfg.powerupEverySeconds);
  set("pwr-charges", cfg.powerupCharges);
  set("pwr-despawn", cfg.powerupDespawnSeconds);
  set("pwr-count", cfg.powerupSpawnCount);
  setChecked("pwr-stack", cfg.powerupStacking);
  setChecked("pwr-combine", cfg.combineWeapons);
  for (const type of POWERUP_TYPES) setChecked(`pup-${type}`, cfg.powerupTypes.includes(type));
  for (const k of ADV_KEYS) set(`adv-${k}`, cfg.adv[k]);
  syncNumberSliders();
  renderWallPicker();
  applyModeVisibility();
}

/**
 * Show only the settings relevant to the selected mode, and hide dependent
 * rows whose parent option is off (fog, power-ups, hazards, destructible walls).
 */
export function applyModeVisibility(): void {
  const mode = ($("mode") as HTMLSelectElement).value;
  const cfg = $("lobby-config");
  const ctf = mode === "ctf";
  const teamBased = mode === "teams" || ctf;
  const on = (id: string) => ($(id) as HTMLInputElement).checked;
  const toggle = (sel: string, hidden: boolean) =>
    cfg.querySelectorAll(sel).forEach((el) => el.classList.toggle("hidden", hidden));
  // Team-only controls (team count, team-kill, spawn-zones) — CTF locks these,
  // so they're hidden there too.
  toggle(".cfg-teams", mode !== "teams");
  toggle(".cfg-haswin", mode === "lms");
  // CTF-only (flags to win) vs the point-scoring/rounds controls it replaces.
  toggle(".cfg-ctf", !ctf);
  toggle(".cfg-nctf", ctf);
  // FFA-only controls (e.g. tank collision)
  toggle(".cfg-ffa", mode !== "ffa");
  // Within CTF, "deliver" shows captures-per-round while the points-scoring modes
  // (conquest/carry) show points-to-win.
  const scoreMode = ctf ? ($("ctf-score-mode") as HTMLSelectElement).value : "";
  const points = scoreMode === "conquest" || scoreMode === "carry";
  toggle(".cfg-deliver", !ctf || scoreMode !== "deliver");
  toggle(".cfg-conquest", !points);
  // Carry forces team-carry on (flags only ride tanks), so its toggle is hidden.
  toggle(".cfg-flagcarry", !ctf || scoreMode === "carry");
  const fogOn = on("fog-of-war");
  toggle(".cfg-fog-on", !fogOn);
  toggle(".cfg-fog-team", !fogOn || !teamBased);
  toggle(".cfg-fog-flag", !fogOn || !ctf);
  // Power-ups master switch hides every dependent row (incl. Advanced tuning),
  // and each power-up's Advanced tuning follows its own spawn toggle.
  const pwr = on("powerups");
  toggle(".cfg-pwr-on", !pwr);
  cfg.querySelectorAll<HTMLElement>(".adv-pup").forEach((el) => {
    el.classList.toggle("hidden", !on(`pup-${el.dataset.pup}`));
  });
  // Hazards: sub-settings only matter with at least one zone; per-type tuning
  // follows its spawn toggle.
  const hz = Number(($("hazard-density") as HTMLInputElement).value) > 0;
  toggle(".cfg-hz-on", !hz);
  toggle(".cfg-hz-lava", !hz || !on("hazard-lava"));
  toggle(".cfg-hz-mud", !hz || !on("hazard-mud"));
  toggle(".cfg-hz-heal", !hz || !on("hazard-heal"));
  // Wall HP tuning only matters when walls can take damage.
  toggle(".cfg-dw-on", !on("destructible-walls"));
}

// ---- Map (walls) image picker ----
// Schematic wall segments per map for the picker thumbnails (100×70 viewBox).
const WALL_THUMB_SEGS: Record<WallStyle, number[][]> = {
  open: [],
  maze: [[35, 8, 35, 45], [35, 45, 68, 45], [68, 20, 68, 45], [18, 24, 50, 24], [50, 24, 50, 38]],
  sparse: [[28, 14, 40, 14], [60, 40, 60, 56], [18, 46, 18, 60], [68, 16, 82, 16], [46, 52, 58, 52]],
  cross: [[50, 16, 50, 54], [24, 35, 76, 35]],
  lshape: [[50, 35, 50, 60], [50, 35, 80, 35]],
  tunnels: [[34, 8, 34, 46], [66, 24, 66, 62]],
  box: [
    [28, 18, 44, 18], [56, 18, 72, 18], // top (centre gap)
    [28, 52, 44, 52], [56, 52, 72, 52], // bottom
    [28, 18, 28, 29], [28, 41, 28, 52], // left
    [72, 18, 72, 29], [72, 41, 72, 52], // right
  ],
  dots: [
    [18, 22, 32, 22], [43, 22, 57, 22], [68, 22, 82, 22],
    [18, 48, 32, 48], [43, 48, 57, 48], [68, 48, 82, 48],
  ],
};

/** Inline SVG thumbnail of a map's wall layout. */
function wallThumb(style: WallStyle): string {
  const lines = WALL_THUMB_SEGS[style]
    .map(([x1, y1, x2, y2]) => `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" />`)
    .join("");
  return (
    `<svg class="wall-thumb" viewBox="0 0 100 70" preserveAspectRatio="xMidYMid meet" aria-hidden="true">` +
    `<rect class="wt-border" x="3" y="3" width="94" height="64" rx="3" />` +
    `<g class="wt-walls">${lines}</g></svg>`
  );
}

/** Select a map, refresh the carousel highlight and notify the config editor. */
function pickWall(style: WallStyle): void {
  const sel = $("walls") as HTMLSelectElement;
  sel.value = style;
  syncWallPicker();
  sel.dispatchEvent(new Event("change", { bubbles: true })); // -> updateConfig
}

/** Highlight the selected map and scroll it into the middle of the strip. */
function syncWallPicker(): void {
  $("wall-picker")
    .querySelectorAll<HTMLButtonElement>(".wall-opt")
    .forEach((b) => {
      const selected = b.dataset.wall === ($("walls") as HTMLSelectElement).value;
      b.classList.toggle("selected", selected);
      if (selected) b.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    });
}

/**
 * Build the map (walls) carousel: one horizontal strip of thumbnails with
 * prev/next arrows that wrap around, syncing selection to the hidden select.
 * The strip is built once; later calls only re-sync the highlight so the
 * scroll position survives config refreshes.
 */
export function renderWallPicker(): void {
  const sel = $("walls") as HTMLSelectElement;
  const picker = $("wall-picker");
  if (!picker.querySelector(".wall-strip")) {
    picker.innerHTML =
      `<button type="button" class="wall-nav" data-dir="-1" aria-label="Previous map">‹</button>` +
      `<div class="wall-strip">` +
      WALL_STYLES.map(
        (s) =>
          `<button type="button" class="wall-opt" data-wall="${s}" title="${WALL_LABEL[s]}">` +
          `${wallThumb(s)}<span>${WALL_LABEL[s]}</span></button>`
      ).join("") +
      `</div>` +
      `<button type="button" class="wall-nav" data-dir="1" aria-label="Next map">›</button>`;
    picker.querySelectorAll<HTMLButtonElement>(".wall-opt").forEach((b) => {
      b.onclick = () => pickWall((b.dataset.wall ?? "maze") as WallStyle);
    });
    picker.querySelectorAll<HTMLButtonElement>(".wall-nav").forEach((b) => {
      b.onclick = () => {
        const step = Number(b.dataset.dir) || 1;
        const i = WALL_STYLES.indexOf(sel.value as WallStyle);
        pickWall(WALL_STYLES[(i + step + WALL_STYLES.length) % WALL_STYLES.length]);
      };
    });
  }
  syncWallPicker();
}

// ---- Per-player movement scheme picker ----
export function applyMoveSetting(): void {
  ($("move-mode") as HTMLSelectElement).value = state.moveMode;
  $("move-hint").textContent =
    state.moveMode === "eight"
      ? "Move any direction; the cannon aims separately."
      : "Drive forward/back and rotate; the cannon aims separately.";
  if (state.input) state.input.eightDir = state.moveMode === "eight";
  renderMovePicker();
}

/** SVG inner content: eight short arrows radiating from the centre. */
function moveEightArrows(): string {
  const cx = 50;
  const cy = 35;
  let s = "";
  for (let k = 0; k < 8; k++) {
    const a = (k * Math.PI) / 4;
    const ux = Math.cos(a);
    const uy = Math.sin(a);
    const x1 = cx + ux * 9;
    const y1 = cy + uy * 9;
    const x2 = cx + ux * 23;
    const y2 = cy + uy * 23;
    const h = 6;
    const ax = x2 + Math.cos(a + Math.PI * 0.82) * h;
    const ay = y2 + Math.sin(a + Math.PI * 0.82) * h;
    const bx = x2 + Math.cos(a - Math.PI * 0.82) * h;
    const by = y2 + Math.sin(a - Math.PI * 0.82) * h;
    s +=
      `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" />` +
      `<polygon points="${x2.toFixed(1)},${y2.toFixed(1)} ${ax.toFixed(1)},${ay.toFixed(1)} ${bx.toFixed(1)},${by.toFixed(1)}" />`;
  }
  return s;
}

// Two control schemes, picked from icons (no dropdown).
const MOVE_OPTS: Array<{ id: "relative" | "eight"; name: string; sub: string; icon: string }> = [
  {
    id: "relative",
    name: "Rotate & drive",
    sub: "Tank-style steering",
    icon:
      `<rect class="mv-body" x="42" y="30" width="16" height="15" rx="2" />` +
      `<line x1="50" y1="31" x2="50" y2="18" />` +
      `<path d="M72 28 A 24 24 0 0 1 72 52" />` +
      `<polygon points="72,53 65,48 76,45" />`,
  },
  {
    id: "eight",
    name: "8-direction",
    sub: "Strafe any way",
    icon: moveEightArrows(),
  },
];

/** Build the movement scheme picker (icons, syncing the hidden select). */
export function renderMovePicker(): void {
  const picker = $("move-picker");
  picker.innerHTML = MOVE_OPTS.map(
    (o) =>
      `<button type="button" class="move-opt${o.id === state.moveMode ? " selected" : ""}" data-move="${o.id}">` +
      `<svg class="move-ic" viewBox="0 0 100 70" aria-hidden="true">${o.icon}</svg>` +
      `<span><b>${o.name}</b><small>${o.sub}</small></span></button>`
  ).join("");
  picker.querySelectorAll<HTMLButtonElement>(".move-opt").forEach((b) => {
    b.onclick = () => {
      const sel = $("move-mode") as HTMLSelectElement;
      sel.value = b.dataset.move ?? "relative";
      sel.dispatchEvent(new Event("change", { bubbles: true }));
    };
  });
}
